import React, { useEffect, useMemo, useState } from "react";
import { courseAPI } from "../services/api";
import { getTourSteps } from "../tours/tourConfig";
import "./GuidedTour.css";

const PADDING = 10;

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const normalize = (value) => String(value || "").toLowerCase().trim();

const resolvePhysicsCourseId = (courses = []) => {
  const exact = courses.find((course) => normalize(course.name) === "physics");
  if (exact?.id) return exact.id;

  const partial = courses.find((course) => normalize(course.name).includes("physics"));
  if (partial?.id) return partial.id;

  return courses[0]?.id || null;
};

const GuidedTour = ({ routeName, currentCourse, onCourseChange }) => {
  const steps = useMemo(() => getTourSteps(routeName), [routeName]);
  const [isOpen, setIsOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState(null);

  const currentStep = steps[stepIndex] || null;

  useEffect(() => {
    setIsOpen(false);
    setStepIndex(0);
    setTargetRect(null);
  }, [routeName]);

  useEffect(() => {
    if (!isOpen || !currentStep) {
      setTargetRect(null);
      return;
    }

    const computeRect = () => {
      if (!currentStep.selector) {
        setTargetRect(null);
        return;
      }

      const target = document.querySelector(currentStep.selector);
      if (!target) {
        setTargetRect(null);
        return;
      }

      target.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
      const rect = target.getBoundingClientRect();
      setTargetRect({
        top: rect.top - PADDING,
        left: rect.left - PADDING,
        width: rect.width + PADDING * 2,
        height: rect.height + PADDING * 2,
      });
    };

    computeRect();
    const intervalId = window.setInterval(computeRect, 400);
    window.addEventListener("resize", computeRect);
    window.addEventListener("scroll", computeRect, true);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("resize", computeRect);
      window.removeEventListener("scroll", computeRect, true);
    };
  }, [currentStep, isOpen]);

  if (!steps.length) return null;

  const openTour = async () => {
    if (!currentCourse && typeof onCourseChange === "function") {
      try {
        const response = await courseAPI.getAll();
        const physicsId = resolvePhysicsCourseId(Array.isArray(response.data) ? response.data : []);
        if (physicsId) {
          onCourseChange(physicsId);
          await new Promise((resolve) => window.setTimeout(resolve, 120));
        }
      } catch (error) {
        console.error("Error preloading tour course:", error);
      }
    }
    setStepIndex(0);
    setIsOpen(true);
  };

  const closeTour = () => {
    setIsOpen(false);
    setStepIndex(0);
    setTargetRect(null);
  };

  const onNext = () => {
    if (stepIndex >= steps.length - 1) {
      closeTour();
      return;
    }
    setStepIndex((prev) => prev + 1);
  };

  const onPrev = () => {
    setStepIndex((prev) => Math.max(0, prev - 1));
  };

  const tooltipStyle = (() => {
    if (!targetRect) {
      return {
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      };
    }

    const estimatedWidth = 360;
    const top = clamp(targetRect.top + targetRect.height + 12, 16, window.innerHeight - 240);
    const left = clamp(targetRect.left, 16, window.innerWidth - estimatedWidth - 16);
    return { top, left };
  })();

  return (
    <>
      <button type="button" className="tour-launch-btn" onClick={openTour} data-tour="tour-launch">
        Start Tour
      </button>

      {isOpen && currentStep && (
        <div className="tour-layer" role="dialog" aria-modal="true">
          <div className="tour-backdrop" onClick={closeTour} />

          {targetRect && (
            <div
              className="tour-highlight"
              style={{
                top: targetRect.top,
                left: targetRect.left,
                width: targetRect.width,
                height: targetRect.height,
              }}
            />
          )}

          <div className="tour-tooltip" style={tooltipStyle}>
            <div className="tour-progress">
              Step {stepIndex + 1} of {steps.length}
            </div>
            <h3>{currentStep.title || "Guided Tour"}</h3>
            <p>{currentStep.body}</p>
            {currentStep.selector && !targetRect && (
              <p className="tour-note">
                {currentStep.missingTargetBody
                  || "Target not visible right now on this screen. Continue for the next step."}
              </p>
            )}
            <div className="tour-actions">
              <button type="button" className="tour-btn ghost" onClick={closeTour}>
                Exit
              </button>
              <button type="button" className="tour-btn ghost" onClick={onPrev} disabled={stepIndex === 0}>
                Back
              </button>
              <button type="button" className="tour-btn primary" onClick={onNext}>
                {stepIndex === steps.length - 1 ? "Finish" : "Next"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GuidedTour;
